import React, {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useState
} from 'react';
import { Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { twMerge } from 'tailwind-merge';

import {MenuMaster} from "../../../models/config/MenuMaster";
import {useCompanyMaster} from "../../../queries";

export type MenuMasterFormRefHandle = {
    submit: () => MenuMaster[] | undefined;
};

export type MenuMasterFormProps = {
    disabledKeyField?: boolean;
    defaultValues?: MenuMaster[];
    className?: string;
};

export const MenuMasterForm = forwardRef<
    MenuMasterFormRefHandle,
    MenuMasterFormProps
>((props, ref) => {
    const { disabledKeyField, defaultValues, className } = props;

    const [formValues, setFormValues] = useState<
        Partial<Pick<MenuMaster, 'id' | 'menu_name'>>
    >(() => {
        const firstDefaultMenu = defaultValues?.[0];
        return {
            id: firstDefaultMenu?.id ?? '',
            menu_name: firstDefaultMenu?.menu_name ?? ''
        };
    });
    const [selectedSIDSet, setSelectedSIDSet] = useState<Set<string>>(
        new Set<string>(defaultValues?.map(menu => menu.sid) ?? [])
    );
    const [errors, setErrors] = useState<Record<keyof MenuMaster, string>>(
        {} as MenuMaster
    );

    const { data: companyMaster } = useCompanyMaster();
    
    const handleFormValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const key = event.currentTarget.name;
        const value = event.currentTarget.value;

        setFormValues({ ...formValues, [key]: value });
    };

    const handleCompanyCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isCheck = event.target.checked;
        if (isCheck) {
            selectedSIDSet.add(event.target.id);
        } else {
            selectedSIDSet.delete(event.target.id);
        }

        setSelectedSIDSet(new Set(selectedSIDSet));
    };

    const submitForm = () => {
        const errors: [keyof MenuMaster, string][] = [];
        if (selectedSIDSet.size === 0) {
            errors.push(['sid', 'กรุณาเลือก Company']);
        }
        if (!formValues.id) {
            errors.push(['id', 'กรุณากรอก Menu ID']);
        }
        if (!formValues.menu_name) {
            errors.push(['menu_name', 'กรุณากรอกชื่อ']);
        }

        if (errors.length > 0) {
            setErrors(prevState => ({
                ...prevState,
                // @ts-ignore
                ...Object.fromEntries(errors)
            }));

            return undefined;
        }

        return Array.from<string, MenuMaster>(
            selectedSIDSet,
            sid =>
                ({
                    sid,
                    id: formValues.id,
                    menu_name: formValues.menu_name
                }) as MenuMaster
        );
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: submitForm
        }),
        [submitForm]
    );

    return (
        <Form className={twMerge('tw-flex tw-flex-col', className)}>
            <FormGroup>
                <Label for="id">Menu ID</Label>
                <Input
                    disabled={disabledKeyField}
                    invalid={!!errors.id}
                    id="id"
                    type="text"
                    name="id"
                    value={formValues.id}
                    onChange={
                        !disabledKeyField ? handleFormValueChange : undefined
                    }
                />
                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="menu_name">Menu Name</Label>
                <Input
                    invalid={!!errors.menu_name}
                    id="menu_name"
                    type="text"
                    name="menu_name"
                    value={formValues.menu_name}
                    onChange={handleFormValueChange}
                />
                <FormFeedback>{errors.menu_name}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label>Company</Label>
                {companyMaster?.map(company => (
                    <div
                        key={company.sid}
                        className="tw-flex tw-items-center tw-gap-2"
                    >
                        <Input
                            id={company.sid}
                            type="checkbox"
                            defaultChecked={selectedSIDSet.has(
                                company.sid
                            )}
                            onChange={handleCompanyCheck}
                            className="tw-static tw-m-0"
                        />
                        <Label
                            for={company.sid}
                            className="tw-m-0 tw-py-0.5"
                        >
                            {company.company_name}
                        </Label>
                    </div>
                ))}
            </FormGroup>
        </Form>
    );
});

MenuMasterForm.displayName = 'MenuMasterForm';
